import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import useMobileOptimization from '../../hooks/useMobileOptimization';
import { 
  Image, 
  Link, 
  X,
  Search,
  Filter,
  Plus,
  Upload,
  Tag,
  Calendar,
  Star,
  Download,
  Eye,
  Bookmark,
  Share2,
  FileText,
  Video,
  BookOpen,
  Scale,
  Users,
  Heart,
  Shield,
  Brain,
  Lightbulb,
  GraduationCap
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import InbuiltPDFViewer from '../../components/InbuiltPDFViewer';

import { collection, getDocs, addDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatFileSize } from '../../firebase/r2storage';
import { formatTimestamp } from '../../utils/timestampUtils';

const Resources = () => {
  const { isMobile, isTablet, mobileUtils } = useMobileOptimization();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCoachingName, setFilterCoachingName] = useState('all');
  const [selectedResource, setSelectedResource] = useState(null);
  const [pdfViewerState, setPdfViewerState] = useState({ open: false, url: null, title: null, minimized: false });
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [pdfViewMethod, setPdfViewMethod] = useState('object'); // 'object', 'embed', 'google', 'iframe'
  const pdfLoadTimeoutRef = useRef(null);
  const pdfViewMethodRef = useRef('object');
  const [showFundamentalDuties, setShowFundamentalDuties] = useState(false);
  const [showCivicSense, setShowCivicSense] = useState(false);
  const [showEthicsIntegrity, setShowEthicsIntegrity] = useState(false);
  const [showCriticalThinking, setShowCriticalThinking] = useState(false);
  const [showEducationTeachers, setShowEducationTeachers] = useState(false);
  const [error, setError] = useState('');

  // Load resources from Firebase
  useEffect(() => {
    loadResourcesFromFirebase();
  }, []);

  const loadResourcesFromFirebase = async () => {
    try {
      setLoading(true);
      // Load resources from Firebase
      const resourcesRef = collection(db, 'resources');
      const resourcesSnapshot = await getDocs(resourcesRef);
      const resourcesData = resourcesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setResources(resourcesData);
      } catch (error) {
      setError('Failed to load resources. Please try again later.');
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  // Get unique coaching names from resources
  const uniqueCoachingNames = Array.isArray(resources) 
    ? [...new Set(resources.map(r => r.coachingName).filter(Boolean))].sort()
    : [];

  // Filter resources
  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (resource.tags && resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    
    const matchesType = filterType === 'all' || resource.type === filterType;
    const matchesCoachingName = filterCoachingName === 'all' || 
                                 resource.coachingName === filterCoachingName;
    
    return matchesSearch && matchesType && matchesCoachingName;
  })
  .sort((a, b) => {
    // Prioritize admin resources (coachingId is null/undefined) over coaching resources
    const aIsAdmin = !a.coachingId;
    const bIsAdmin = !b.coachingId;
    
    if (aIsAdmin && !bIsAdmin) return -1; // Admin resources first
    if (!aIsAdmin && bIsAdmin) return 1;  // Coaching resources after admin
    
    // If both are same type, sort by createdAt descending (newest first)
    const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
    const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });

  const getResourceIcon = (type) => {
    switch (type) {
      case 'document': return <FileText className="w-6 h-6" />;
      case 'video': return <Video className="w-6 h-6" />;
      case 'image': return <Image className="w-6 h-6" />;
      case 'link': return <Link className="w-6 h-6" />;
      default: return <BookOpen className="w-6 h-6" />;
    }
  };

  const getResourceTypeColor = (type) => {
    switch (type) {
      case 'document': return 'bg-blue-100 text-blue-800';
      case 'video': return 'bg-purple-100 text-purple-800';
      case 'image': return 'bg-green-100 text-green-800';
      case 'link': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const types = ['document', 'video', 'image', 'link'];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className={`${mobileUtils.getContainerClasses()} pb-4 sm:pb-6 pt-2`}>
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="px-1">
              <h1 className={`font-bold text-gray-900 dark:text-white ${mobileUtils.getTextClasses('xl')}`}>Resources</h1>
              <p className={`text-gray-600 dark:text-gray-400 ${mobileUtils.getTextClasses('sm')}`}>Access educational materials and study resources</p>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center min-h-[44px] text-sm"
              >
                <Search className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>Refresh Resources</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className={`${mobileUtils.getContainerClasses()} pb-4 pt-2`}>
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={(e) => mobileUtils.preventZoom(e.target)}
                  className="w-full pl-10 pr-4 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base sm:text-sm min-h-[44px]"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base sm:text-sm min-h-[44px] flex-1 sm:flex-none"
              >
                <option value="all">All Types</option>
                {types.map(type => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>

              <select
                value={filterCoachingName}
                onChange={(e) => setFilterCoachingName(e.target.value)}
                className="px-3 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base sm:text-sm min-h-[44px] flex-1 sm:flex-none"
              >
                <option value="all">All Coachings</option>
                {uniqueCoachingNames.map(coachingName => (
                  <option key={coachingName} value={coachingName}>{coachingName}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-2">
        {/* Educational Cards - Always on Top */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {/* Fundamental Duties Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
            onClick={() => setShowFundamentalDuties(true)}
            className="bg-gradient-to-br from-orange-50 to-green-50 dark:from-orange-900/20 dark:to-green-900/20 rounded-lg shadow-sm border-2 border-orange-200 dark:border-orange-700 overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
          >
            <div className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="p-1.5 rounded-lg bg-orange-100 dark:bg-orange-800 text-orange-600 dark:text-orange-300">
                  <Scale className="w-4 h-4" />
                </div>
              </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">
                  Fundamental Duties
                </h3>
                
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                  Read the 11 Fundamental Duties of Indian citizens as per Article 51A
                </p>

                <div className="flex flex-wrap gap-1 mb-2">
                  <span className="px-1.5 py-0.5 text-[10px] bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-300 rounded-full">
                    Constitution
                  </span>
                </div>
              </div>

              <div className="border-t border-orange-200 dark:border-orange-700 p-2 bg-white/50 dark:bg-gray-800/50">
                <button className="w-full px-2 py-1.5 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center justify-center">
                  <Eye className="w-3 h-3 mr-1" />
                  View
                </button>
              </div>
          </motion.div>

          {/* Civic Sense Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
            onClick={() => setShowCivicSense(true)}
            className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg shadow-sm border-2 border-blue-200 dark:border-blue-700 overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
          >
            <div className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300">
                  <Users className="w-4 h-4" />
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">
                Civic Sense
              </h3>
              
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                Learn essential behaviors and practices that make our society better
              </p>

              <div className="flex flex-wrap gap-1 mb-2">
                <span className="px-1.5 py-0.5 text-[10px] bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-full">
                  Society
                </span>
              </div>
            </div>

            <div className="border-t border-blue-200 dark:border-blue-700 p-2 bg-white/50 dark:bg-gray-800/50">
              <button className="w-full px-2 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center">
                <Eye className="w-3 h-3 mr-1" />
                View
              </button>
            </div>
          </motion.div>

          {/* Ethics, Integrity & Emotional Intelligence Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
            onClick={() => setShowEthicsIntegrity(true)}
            className="bg-gradient-to-br from-indigo-50 to-pink-50 dark:from-indigo-900/20 dark:to-pink-900/20 rounded-lg shadow-sm border-2 border-indigo-200 dark:border-indigo-700 overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
          >
            <div className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="p-1.5 rounded-lg bg-indigo-100 dark:bg-indigo-800 text-indigo-600 dark:text-indigo-300">
                  <Brain className="w-4 h-4" />
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">
                Ethics, Integrity & EQ
              </h3>
              
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                Master the three pillars of personal and professional excellence
              </p>

              <div className="flex flex-wrap gap-1 mb-2">
                <span className="px-1.5 py-0.5 text-[10px] bg-indigo-100 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-full">
                  Ethics
                </span>
              </div>
            </div>

            <div className="border-t border-indigo-200 dark:border-indigo-700 p-2 bg-white/50 dark:bg-gray-800/50">
              <button className="w-full px-2 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center justify-center">
                <Eye className="w-3 h-3 mr-1" />
                View
              </button>
            </div>
          </motion.div>

          {/* Critical Thinking & Questioning Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
            onClick={() => setShowCriticalThinking(true)}
            className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg shadow-sm border-2 border-violet-200 dark:border-violet-700 overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
          >
            <div className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-800 text-violet-600 dark:text-violet-300">
                  <Lightbulb className="w-4 h-4" />
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">
                Critical Thinking
              </h3>
              
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                Develop analytical skills and learn to ask the right questions
              </p>

              <div className="flex flex-wrap gap-1 mb-2">
                <span className="px-1.5 py-0.5 text-[10px] bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded-full">
                  Analysis
                </span>
              </div>
            </div>

            <div className="border-t border-violet-200 dark:border-violet-700 p-2 bg-white/50 dark:bg-gray-800/50">
              <button className="w-full px-2 py-1.5 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 flex items-center justify-center">
                <Eye className="w-3 h-3 mr-1" />
                View
              </button>
            </div>
          </motion.div>

          {/* Education & Teachers Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
            onClick={() => setShowEducationTeachers(true)}
            className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-lg shadow-sm border-2 border-teal-200 dark:border-teal-700 overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
          >
            <div className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="p-1.5 rounded-lg bg-teal-100 dark:bg-teal-800 text-teal-600 dark:text-teal-300">
                  <GraduationCap className="w-4 h-4" />
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">
                Education & Teachers
              </h3>
              
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                Understanding the value of education and respecting teachers
              </p>

              <div className="flex flex-wrap gap-1 mb-2">
                <span className="px-1.5 py-0.5 text-[10px] bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-300 rounded-full">
                  Learning
                </span>
              </div>
            </div>

            <div className="border-t border-teal-200 dark:border-teal-700 p-2 bg-white/50 dark:bg-gray-800/50">
              <button className="w-full px-2 py-1.5 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center justify-center">
                <Eye className="w-3 h-3 mr-1" />
                View
              </button>
            </div>
          </motion.div>
        </div>

        {/* Regular Resources */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredResources.map((resource) => (
          <motion.div
            key={resource.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-200"
          >
            {/* Resource Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${getResourceTypeColor(resource.type)}`}>
                      {getResourceIcon(resource.type)}
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setSelectedResource(resource)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          // Share functionality
                          if (navigator.share) {
                            navigator.share({
                              title: resource.title,
                              text: resource.description,
                              url: resource.fileUrl
                            });
                          } else {
                            // Fallback: copy to clipboard
                            navigator.clipboard.writeText(resource.fileUrl);
                          }
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Share"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {resource.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                    {resource.description}
                  </p>

                                    {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {Array.isArray(resource.tags) ? (
                      <>
                        {resource.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {resource.tags.length > 3 && (
                          <span className="px-2 py-1 text-xs text-gray-500">
                            +{resource.tags.length - 3}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                        {resource.tags || 'No tags'}
                      </span>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-2">
                      <span className="capitalize">{resource.category}</span>
                      <span>•</span>
                      <span>{resource.fileSize}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Star className="w-3 h-3 text-yellow-500 fill-current" />
                      <span>{resource.rating}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatTimestamp(resource.uploadDate, { includeTime: false, short: true })}</span>
                      </div>
                      <div className="mt-1">
                        {resource.downloads} downloads
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {(() => {
                        const resourceUrl = resource.fileUrl || resource.files?.[0]?.url || resource.url;
                        const isPDF = resourceUrl?.toLowerCase().endsWith('.pdf') || resource.type === 'application/pdf' || resource.type === 'pdf' || resource.files?.[0]?.type === 'application/pdf';
                        return isPDF ? (
                          <button
                            onClick={() => {
                              if (resourceUrl) {
                                setPdfLoadError(false);
                                setPdfViewMethod('object'); // Start with object tag for Chrome's native viewer
                                pdfViewMethodRef.current = 'object';
                                setPdfViewerState({ open: true, url: resourceUrl, title: resource.title, minimized: false });
                                // Set timeout to detect if PDF doesn't load
                                if (pdfLoadTimeoutRef.current) {
                                  clearTimeout(pdfLoadTimeoutRef.current);
                                }
                                pdfLoadTimeoutRef.current = setTimeout(() => {
                                  // Try next method if timeout
                                  const currentMethod = pdfViewMethodRef.current;
                                  if (currentMethod === 'object') {
                                    setPdfViewMethod('embed');
                                    pdfViewMethodRef.current = 'embed';
                                  } else if (currentMethod === 'embed') {
                                    setPdfViewMethod('iframe');
                                    pdfViewMethodRef.current = 'iframe';
                                  } else if (currentMethod === 'iframe') {
                                    setPdfViewMethod('google');
                                    pdfViewMethodRef.current = 'google';
                                  } else {
                                    setPdfLoadError(true);
                                  }
                                }, 8000); // 8 second timeout
                              } else {
                                alert('PDF URL is not available. Please try again later.');
                              }
                            }}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </button>
                        ) : (
                          <button
                            onClick={() => setSelectedResource(resource)}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </button>
                        );
                      })()}

                      <BookmarkButton
                        itemId={resource.id}
                        type="resource"
                        title={resource.title}
                        description={resource.description}
                        url={resource.fileUrl || resource.files?.[0]?.url || resource.url}
                        thumbnail={resource.thumbnail}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Resource Detail Modal */}
      {selectedResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Resource Details</h2>
              <button
                onClick={() => setSelectedResource(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg ${getResourceTypeColor(selectedResource.type)}`}>
                  {getResourceIcon(selectedResource.type)}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {selectedResource.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-3">
                    {selectedResource.description}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Category:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedResource.category || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 capitalize">{selectedResource.type || 'N/A'}</span>
                </div>
                {selectedResource.coachingName && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Coaching:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedResource.coachingName}</span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Downloads:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedResource.downloads || 0}</span>
                </div>
                {selectedResource.rating && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Rating:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400 flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 fill-current mr-1" />
                      {selectedResource.rating}
                    </span>
                  </div>
                )}
                {(selectedResource.uploadDate || selectedResource.createdAt) && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Uploaded:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">
                      {formatTimestamp(selectedResource.uploadDate || selectedResource.createdAt, { includeTime: false, short: true })}
                    </span>
                  </div>
                )}
              </div>

              {selectedResource.tags && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Tags:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Array.isArray(selectedResource.tags) ? (
                      selectedResource.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                        {selectedResource.tags || 'No tags'}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Files Display */}
              {selectedResource.files && selectedResource.files.length > 0 ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    Files ({selectedResource.files.length})
                  </h3>
                  <div className="space-y-4">
                    {selectedResource.files.map((file, index) => {
                      const isPDF = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
                      const isImage = file.type?.startsWith('image/');
                      const isVideo = file.type?.startsWith('video/');
                      const fileUrl = file.url || file.downloadURL || file.fileUrl;

                      return (
                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              {isPDF && <FileText className="w-6 h-6 text-red-500" />}
                              {isImage && <Image className="w-6 h-6 text-blue-500" />}
                              {isVideo && <Video className="w-6 h-6 text-purple-500" />}
                              {!isPDF && !isImage && !isVideo && <FileText className="w-6 h-6 text-gray-500" />}
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                                {file.size && (
                                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                )}
                              </div>
                            </div>
                            {!isPDF && (
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Open
                              </a>
                            )}
                            {isPDF && (
                              <button
                                onClick={() => {
                                  if (fileUrl) {
                                    setPdfLoadError(false);
                                    setPdfViewMethod('object'); // Start with object tag for Chrome's native viewer
                                    setPdfViewerState({ open: true, url: fileUrl, title: file.name || selectedResource.title, minimized: false });
                                    // Set timeout to detect if PDF doesn't load
                                    if (pdfLoadTimeoutRef.current) {
                                      clearTimeout(pdfLoadTimeoutRef.current);
                                    }
                                    pdfLoadTimeoutRef.current = setTimeout(() => {
                                      // Try next method if timeout
                                      if (pdfViewMethod === 'object') {
                                        setPdfViewMethod('embed');
                                      } else if (pdfViewMethod === 'embed') {
                                        setPdfViewMethod('iframe');
                                      } else if (pdfViewMethod === 'iframe') {
                                        setPdfViewMethod('google');
                                      } else {
                                        setPdfLoadError(true);
                                      }
                                    }, 8000); // 8 second timeout
                                  } else {
                                    alert('PDF URL is not available. Please try again later.');
                                  }
                                }}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1"
                              >
                                <Eye className="w-4 h-4" />
                                <span>View</span>
                              </button>
                            )}
                          </div>

                          {/* Preview */}
                          {fileUrl && !isPDF && (
                            <div className="mt-3">
                              {isImage && (
                                <img
                                  src={fileUrl}
                                  alt={file.name}
                                  className="max-w-full h-auto rounded border border-gray-200 dark:border-gray-700"
                                  onError={(e) => {
                                    e.target.alt = 'Image failed to load. Click "Open" to view in a new tab.';
                                    e.target.style.border = '2px dashed #ccc';
                                  }}
                                />
                              )}
                              {isVideo && (
                                <video
                                  src={fileUrl}
                                  controls
                                  className="w-full rounded border border-gray-200 dark:border-gray-700"
                                >
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : selectedResource.fileUrl || selectedResource.url ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Resource Link</h3>
                  {(() => {
                    const resourceUrl = selectedResource.fileUrl || selectedResource.url;
                    const isPDF = resourceUrl?.toLowerCase().endsWith('.pdf') || selectedResource.type === 'application/pdf' || selectedResource.type === 'pdf';
                    return isPDF ? (
                      <button
                        onClick={() => {
                          if (resourceUrl) {
                            setPdfLoadError(false);
                            setPdfViewMethod('object'); // Start with object tag for Chrome's native viewer
                            setPdfViewerState({ open: true, url: resourceUrl, title: selectedResource.title, minimized: false });
                            // Set timeout to detect if PDF doesn't load
                            if (pdfLoadTimeoutRef.current) {
                              clearTimeout(pdfLoadTimeoutRef.current);
                            }
                            pdfLoadTimeoutRef.current = setTimeout(() => {
                              // Try next method if timeout
                              if (pdfViewMethod === 'object') {
                                setPdfViewMethod('embed');
                              } else if (pdfViewMethod === 'embed') {
                                setPdfViewMethod('iframe');
                              } else if (pdfViewMethod === 'iframe') {
                                setPdfViewMethod('google');
                              } else {
                                setPdfLoadError(true);
                              }
                            }, 8000); // 8 second timeout
                          } else {
                            alert('PDF URL is not available. Please try again later.');
                          }
                        }}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View PDF</span>
                      </button>
                    ) : (
                      <a
                        href={resourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline break-all"
                      >
                        {resourceUrl}
                      </a>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No files or links available for this resource</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    const shareUrl = selectedResource.fileUrl || selectedResource.files?.[0]?.url || selectedResource.url;
                    if (navigator.share) {
                      navigator.share({
                        title: selectedResource.title,
                        text: selectedResource.description,
                        url: shareUrl
                      });
                    } else {
                      navigator.clipboard.writeText(shareUrl);
                    }
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </button>
                {(selectedResource.fileUrl || selectedResource.files?.[0]?.url || selectedResource.url) && (
                  <button
                    onClick={() => window.open(selectedResource.fileUrl || selectedResource.files?.[0]?.url || selectedResource.url, '_blank')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Open in New Tab
                  </button>
                )}

                <BookmarkButton
                  itemId={selectedResource.id}
                  type="resource"
                  title={selectedResource.title}
                  description={selectedResource.description}
                  url={selectedResource.fileUrl || selectedResource.files?.[0]?.url || selectedResource.url}
                  thumbnail={selectedResource.thumbnail}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fundamental Duties Modal */}
      {showFundamentalDuties && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-green-500 text-white p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Scale className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">Fundamental Duties</h2>
                    <p className="text-sm text-white/90 mt-1">Article 51A of the Indian Constitution</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFundamentalDuties(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Note:</strong> Fundamental Duties are moral obligations and not legally enforceable. 
                  They serve as a reminder to citizens to uphold the values enshrined in the Constitution.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  {
                    number: 1,
                    duty: "To abide by the Constitution and respect its ideals and institutions, the National Flag and the National Anthem"
                  },
                  {
                    number: 2,
                    duty: "To cherish and follow the noble ideals which inspired our national struggle for freedom"
                  },
                  {
                    number: 3,
                    duty: "To uphold and protect the sovereignty, unity and integrity of India"
                  },
                  {
                    number: 4,
                    duty: "To defend the country and render national service when called upon to do so"
                  },
                  {
                    number: 5,
                    duty: "To promote harmony and the spirit of common brotherhood amongst all the people of India transcending religious, linguistic and regional or sectional diversities; to renounce practices derogatory to the dignity of women"
                  },
                  {
                    number: 6,
                    duty: "To value and preserve the rich heritage of our composite culture"
                  },
                  {
                    number: 7,
                    duty: "To protect and improve the natural environment including forests, lakes, rivers, wildlife and to have compassion for living creatures"
                  },
                  {
                    number: 8,
                    duty: "To develop the scientific temper, humanism and the spirit of inquiry and reform"
                  },
                  {
                    number: 9,
                    duty: "To safeguard public property and to abjure violence"
                  },
                  {
                    number: 10,
                    duty: "To strive towards excellence in all spheres of individual and collective activity so that the nation constantly rises to higher levels of endeavour and achievement"
                  },
                  {
                    number: 11,
                    duty: "To provide opportunities for education to his child or ward between the age of six and fourteen years (added by 86th Amendment Act, 2002)"
                  }
                ].map((item) => (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: item.number * 0.05 }}
                    className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-green-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {item.number}
                    </div>
                    <p className="flex-1 text-gray-700 dark:text-gray-300 leading-relaxed">
                      {item.duty}
                    </p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Historical Context</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  The Fundamental Duties were added to the Constitution by the 42nd Amendment Act, 1976, 
                  on the recommendation of the Swaran Singh Committee. Originally there were 10 duties, 
                  and the 11th duty was added by the 86th Amendment Act, 2002.
                </p>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowFundamentalDuties(false)}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Civic Sense Modal */}
      {showCivicSense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Users className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">Civic Sense</h2>
                    <p className="text-sm text-white/90 mt-1">Building a Better Society Together</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCivicSense(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>What is Civic Sense?</strong> Civic sense is the awareness and behavior that helps maintain order, 
                  respect, and consideration in society. It's about being mindful of how our actions affect others and 
                  contributing positively to our community.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  {
                    title: "Respect Public Property",
                    description: "Treat public facilities with care and respect",
                    examples: "Don't write on walls, don't damage public benches, keep parks clean, report vandalism to authorities"
                  },
                  {
                    title: "Follow Traffic Rules",
                    description: "Obey traffic signals and road regulations for everyone's safety",
                    examples: "Stop at red lights, wear helmets while riding, use zebra crossings, don't jump signals, follow lane discipline"
                  },
                  {
                    title: "Maintain Queues",
                    description: "Wait in line patiently and respect the order",
                    examples: "Stand in queue at ticket counters, ATMs, bus stops, don't push or cut in line, maintain proper distance"
                  },
                  {
                    title: "Dispose Waste Properly",
                    description: "Use dustbins and segregate waste for recycling",
                    examples: "Throw trash in bins, don't litter on roads, separate wet and dry waste, use designated dumping areas"
                  },
                  {
                    title: "Control Noise Pollution",
                    description: "Keep noise levels low, especially in residential areas",
                    examples: "Avoid loud music late at night, use horns only when necessary, keep phone conversations quiet in public, respect quiet hours"
                  },
                  {
                    title: "Respect Public Spaces",
                    description: "Keep shared spaces clean and comfortable for everyone",
                    examples: "Don't spit in public, don't urinate in open spaces, keep parks and gardens clean, don't smoke in prohibited areas"
                  },
                  {
                    title: "Help Others When Possible",
                    description: "Offer assistance to those in need",
                    examples: "Help elderly cross roads, offer seats to pregnant women, assist during emergencies, volunteer for community service"
                  },
                  {
                    title: "Follow Social Distancing",
                    description: "Maintain appropriate distance in public places",
                    examples: "Keep distance in queues, avoid crowding, respect personal space, especially during health emergencies"
                  },
                  {
                    title: "Use Public Transport Respectfully",
                    description: "Be considerate of fellow passengers",
                    examples: "Give up seats for elderly/pregnant, don't block doorways, keep luggage out of walkways, don't play loud music"
                  },
                  {
                    title: "Respect Quiet Zones",
                    description: "Maintain silence in designated areas",
                    examples: "Keep quiet in libraries, hospitals, educational institutions, maintain silence during exams, respect prayer times"
                  },
                  {
                    title: "Don't Block Public Pathways",
                    description: "Keep footpaths and roads clear for everyone",
                    examples: "Don't park vehicles on footpaths, don't block entrances, keep sidewalks clear, don't display goods on public spaces"
                  },
                  {
                    title: "Use Water Wisely",
                    description: "Conserve water as it's a precious resource",
                    examples: "Don't waste water, fix leaking taps, use water responsibly, report water wastage, practice rainwater harvesting"
                  },
                  {
                    title: "Respect Traffic Signs",
                    description: "Follow all road signs and markers",
                    examples: "Obey speed limits, follow one-way signs, respect parking restrictions, don't park in no-parking zones"
                  },
                  {
                    title: "Be Punctual",
                    description: "Respect others' time by being on time",
                    examples: "Arrive on time for appointments, meetings, classes, don't keep others waiting unnecessarily"
                  },
                  {
                    title: "Don't Smoke in Public Places",
                    description: "Respect non-smokers and follow smoking regulations",
                    examples: "Use designated smoking areas, don't smoke near children, dispose cigarette butts properly, respect no-smoking zones"
                  },
                  {
                    title: "Respect Others' Privacy",
                    description: "Don't intrude into others' personal space or affairs",
                    examples: "Don't stare at others, respect personal boundaries, don't take photos without permission, maintain confidentiality"
                  },
                  {
                    title: "Keep Public Toilets Clean",
                    description: "Maintain hygiene in shared facilities",
                    examples: "Flush after use, don't leave trash, report maintenance issues, use facilities properly"
                  },
                  {
                    title: "Follow COVID-19 Protocols",
                    description: "Adhere to health guidelines for public safety",
                    examples: "Wear masks when required, maintain hygiene, get vaccinated, follow isolation guidelines when sick"
                  },
                  {
                    title: "Don't Use Mobile Phones Inappropriately",
                    description: "Be mindful of phone usage in public",
                    examples: "Don't talk loudly on phone, switch to silent mode in public places, don't use phone while driving, respect others' conversations"
                  },
                  {
                    title: "Respect Cultural and Religious Places",
                    description: "Maintain decorum at places of worship and cultural sites",
                    examples: "Follow dress codes, remove shoes where required, maintain silence, respect traditions, don't damage monuments"
                  },
                  {
                    title: "Don't Feed Animals in Public",
                    description: "Follow guidelines for animal interaction",
                    examples: "Don't feed stray animals inappropriately, don't disturb wildlife, follow zoo rules, report animal abuse"
                  },
                  {
                    title: "Participate in Community Cleanliness",
                    description: "Take part in keeping your area clean",
                    examples: "Join cleanliness drives, participate in tree planting, volunteer for community work, organize neighborhood cleanups"
                  },
                  {
                    title: "Respect Emergency Services",
                    description: "Give way to emergency vehicles and personnel",
                    examples: "Give way to ambulances, fire trucks, don't block emergency exits, follow emergency personnel instructions, don't misuse emergency numbers"
                  },
                  {
                    title: "Use Public Facilities Responsibly",
                    description: "Use shared amenities without causing damage",
                    examples: "Don't misuse public utilities, report damages, use facilities as intended, don't overload systems"
                  },
                  {
                    title: "Be a Responsible Digital Citizen",
                    description: "Practice good behavior online and with technology",
                    examples: "Don't share fake news, respect others online, don't misuse technology, protect personal data, report cybercrime"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-l-4 border-blue-500"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {item.description}
                        </p>
                        <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            <strong>Examples:</strong>
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            {item.examples}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Why Civic Sense Matters</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Civic sense is not just about following rules—it's about creating a society where everyone feels respected, 
                  safe, and valued. When we practice good civic sense:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                  <li>We create a cleaner, more pleasant environment for everyone</li>
                  <li>We reduce conflicts and improve social harmony</li>
                  <li>We set positive examples for future generations</li>
                  <li>We contribute to the overall development and progress of our nation</li>
                  <li>We make public spaces more accessible and enjoyable for all</li>
                </ul>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowCivicSense(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Ethics, Integrity & Emotional Intelligence Modal */}
      {showEthicsIntegrity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gradient-to-r from-indigo-500 via-pink-500 to-purple-500 text-white p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Brain className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">Ethics, Integrity & Emotional Intelligence</h2>
                    <p className="text-sm text-white/90 mt-1">Building Character and Wisdom</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEthicsIntegrity(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Introduction */}
              <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-500 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Understanding the Three Pillars:</strong> Ethics, Integrity, and Emotional Intelligence are 
                  interconnected qualities that form the foundation of strong character and successful relationships. 
                  Together, they guide us in making wise decisions, building trust, and navigating life's challenges with wisdom.
                </p>
              </div>

              {/* ETHICS SECTION */}
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">1. Ethics</h2>
                </div>
                
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">What is Ethics?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ethics are moral principles that govern a person's behavior or conduct. They help us distinguish 
                    between right and wrong, guiding our decisions and actions in various situations.
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-indigo-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Key Principles:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside">
                      <li><strong>Honesty:</strong> Being truthful in all interactions</li>
                      <li><strong>Fairness:</strong> Treating everyone equally and justly</li>
                      <li><strong>Respect:</strong> Valuing others' rights and dignity</li>
                      <li><strong>Responsibility:</strong> Being accountable for your actions</li>
                      <li><strong>Compassion:</strong> Showing empathy and kindness</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-indigo-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Practical Actions:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li className="flex items-start"><span className="mr-2">•</span> Always tell the truth, even when it's difficult</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Give credit where it's due - acknowledge others' contributions</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Report unethical behavior you witness</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Make decisions considering the impact on others</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Stand up for what's right, even if unpopular</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-indigo-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Real-World Examples:</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Academic:</strong> You find a USB drive with exam answers. Ethical action: Return it to the teacher 
                          or lost & found without looking at the content, even if it could help your grades.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Workplace:</strong> Your colleague takes credit for your idea in a meeting. Ethical response: 
                          Politely clarify your contribution in a professional manner, or discuss privately with your manager.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Social:</strong> You see someone being bullied. Ethical action: Intervene safely or report to 
                          authorities, showing compassion for the victim.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-indigo-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Different Points of View:</h4>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <p><strong>Consequentialist:</strong> "The right action is one that produces the best overall outcome." 
                      Example: Sometimes a white lie prevents unnecessary hurt.</p>
                      <p><strong>Deontological:</strong> "Some actions are inherently right or wrong, regardless of consequences." 
                      Example: Lying is always wrong, even if it helps someone.</p>
                      <p><strong>Virtue Ethics:</strong> "Focus on developing good character traits." Example: Be honest because 
                      honesty is a virtue that makes you a better person.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* INTEGRITY SECTION */}
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">2. Integrity</h2>
                </div>
                
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">What is Integrity?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Integrity is the quality of being honest and having strong moral principles. It means doing the right thing 
                    even when no one is watching, and maintaining consistency between your values and actions.
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-pink-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Core Characteristics:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside">
                      <li><strong>Consistency:</strong> Your actions match your words and values</li>
                      <li><strong>Reliability:</strong> Others can count on you to keep your promises</li>
                      <li><strong>Authenticity:</strong> Being genuine and true to yourself</li>
                      <li><strong>Courage:</strong> Standing firm in your principles despite pressure</li>
                      <li><strong>Self-Accountability:</strong> Taking responsibility without excuses</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-pink-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Practical Actions:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li className="flex items-start"><span className="mr-2">•</span> Keep promises, even small ones</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Admit mistakes openly and learn from them</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Don't compromise your values for convenience</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Be the same person in public and private</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Return extra change if a cashier makes a mistake</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Complete tasks you commit to, even when difficult</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-pink-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Real-World Examples:</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Academic Integrity:</strong> You're struggling with an assignment and a friend offers you their completed work. 
                          Showing integrity means declining, doing your own work, and asking for help instead of copying.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Financial Integrity:</strong> You notice a billing error in your favor at a restaurant. Integrity means 
                          pointing it out and paying the correct amount, even though no one would know.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Social Integrity:</strong> You promise to help a friend move, but something better comes up. 
                          Integrity means keeping your commitment, even if it's inconvenient.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-pink-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Different Points of View:</h4>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <p><strong>Personal Integrity:</strong> "I do what's right because it aligns with my values." 
                      Example: Returning a lost wallet because you believe in honesty.</p>
                      <p><strong>Professional Integrity:</strong> "I maintain standards in my work regardless of external pressure." 
                      Example: Not cutting corners on quality even under tight deadlines.</p>
                      <p><strong>Social Integrity:</strong> "I treat everyone with respect, regardless of their status." 
                      Example: Being kind to both your boss and the janitor equally.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* EMOTIONAL INTELLIGENCE SECTION */}
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Heart className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">3. Emotional Intelligence (EQ)</h2>
                </div>
                
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">What is Emotional Intelligence?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Emotional Intelligence is the ability to recognize, understand, manage, and effectively use your own emotions 
                    and those of others. It's about being aware of feelings and using that awareness to guide thinking and behavior.
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Five Key Components:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li><strong>Self-Awareness:</strong> Recognizing your own emotions and their impact</li>
                      <li><strong>Self-Regulation:</strong> Managing your emotions and impulses effectively</li>
                      <li><strong>Motivation:</strong> Using emotions to achieve goals and maintain optimism</li>
                      <li><strong>Empathy:</strong> Understanding and sharing others' feelings</li>
                      <li><strong>Social Skills:</strong> Building relationships and managing social interactions</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Practical Actions:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li className="flex items-start"><span className="mr-2">•</span> Pause before reacting when you feel angry or upset</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Practice active listening - focus fully on what others are saying</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Label your emotions - "I'm feeling frustrated" helps you understand yourself</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Consider others' perspectives before responding</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Use "I" statements: "I feel..." instead of "You always..."</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Take deep breaths when emotions are high</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Ask yourself: "How would I feel in their situation?"</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Real-World Examples:</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Conflict Resolution:</strong> Your friend cancels plans last minute. Instead of getting angry, you recognize 
                          your disappointment, consider they might have a valid reason, and calmly ask what happened. This shows self-regulation and empathy.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Team Work:</strong> A teammate seems stressed and is being short with others. You notice their body language 
                          and tone, approach them privately, and ask if everything is okay. This demonstrates empathy and social awareness.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Self-Management:</strong> You receive a poor grade and feel disappointed. Instead of blaming others, you 
                          acknowledge your feelings, identify what went wrong, and create a plan to improve. This shows self-awareness and motivation.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Different Points of View:</h4>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <p><strong>Personal EQ:</strong> "I understand my emotions and can manage them effectively." 
                      Example: Recognizing when you're stressed and taking a break instead of lashing out.</p>
                      <p><strong>Interpersonal EQ:</strong> "I can read and respond to others' emotional needs." 
                      Example: Noticing a friend is sad and offering support without them asking.</p>
                      <p><strong>Social EQ:</strong> "I navigate group dynamics and social situations effectively." 
                      Example: Mediating a disagreement between friends by understanding each person's perspective.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* INTERCONNECTION SECTION */}
              <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50 via-pink-50 to-purple-50 dark:from-indigo-900/20 dark:via-pink-900/20 dark:to-purple-900/20 rounded-lg border border-indigo-200 dark:border-indigo-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">How They Work Together</h3>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <p>
                    <strong>Ethics + Integrity:</strong> Your ethical principles (doing what's right) combined with integrity 
                    (acting on those principles consistently) create a trustworthy character. Example: You believe in honesty (ethics) 
                    and always tell the truth even when it's hard (integrity).
                  </p>
                  <p>
                    <strong>Integrity + Emotional Intelligence:</strong> When you have integrity but lack EQ, you might be honest 
                    but hurtful. With EQ, you can be truthful in a way that's considerate. Example: Giving constructive feedback 
                    that's honest but delivered with empathy.
                  </p>
                  <p>
                    <strong>Ethics + Emotional Intelligence:</strong> Ethics tells you what's right, EQ helps you understand how your 
                    actions affect others emotionally. Example: You know lying is wrong (ethics), and EQ helps you understand 
                    how a lie would hurt someone's trust and feelings.
                  </p>
                  <p className="font-medium text-gray-700 dark:text-gray-300 mt-4">
                    <strong>The Complete Picture:</strong> When all three work together, you make ethical decisions (ethics), 
                    consistently act on them (integrity), and do so in a way that's emotionally aware and considerate (EQ). 
                    This creates strong relationships, builds trust, and leads to personal and professional success.
                  </p>
                </div>
              </div>

              {/* DEVELOPMENT TIPS */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Building These Skills:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">For Ethics:</h4>
                    <ul className="text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                      <li>Read about moral philosophy</li>
                      <li>Reflect on ethical dilemmas</li>
                      <li>Discuss values with mentors</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">For Integrity:</h4>
                    <ul className="text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                      <li>Keep commitments, no matter how small</li>
                      <li>Practice self-reflection daily</li>
                      <li>Surround yourself with people of integrity</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">For EQ:</h4>
                    <ul className="text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                      <li>Practice mindfulness and meditation</li>
                      <li>Keep an emotion journal</li>
                      <li>Seek feedback on your interactions</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowEthicsIntegrity(false)}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Critical Thinking & Questioning Modal */}
      {showCriticalThinking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gradient-to-r from-violet-500 to-purple-500 text-white p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Lightbulb className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">Critical Thinking & Questioning</h2>
                    <p className="text-sm text-white/90 mt-1">Think Clearly, Question Wisely</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCriticalThinking(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 p-4 bg-violet-50 dark:bg-violet-900/20 border-l-4 border-violet-500 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>What is Critical Thinking?</strong> Critical thinking is the ability to analyze information objectively, 
                  evaluate evidence, and make reasoned judgments. It involves questioning assumptions, recognizing biases, and 
                  developing well-supported conclusions.
                </p>
              </div>

              {/* Critical Thinking Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Lightbulb className="w-6 h-6 text-violet-600 dark:text-violet-400 mr-3" />
                  Critical Thinking Skills
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-violet-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Key Components:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside">
                      <li><strong>Analysis:</strong> Breaking down complex information into parts</li>
                      <li><strong>Evaluation:</strong> Assessing the credibility and relevance of information</li>
                      <li><strong>Inference:</strong> Drawing logical conclusions from evidence</li>
                      <li><strong>Explanation:</strong> Clearly communicating your reasoning</li>
                      <li><strong>Self-Regulation:</strong> Reflecting on your own thinking process</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-violet-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Practical Actions:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li className="flex items-start"><span className="mr-2">•</span> Question sources: Who wrote this? What's their agenda?</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Look for evidence before accepting claims</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Consider multiple perspectives on issues</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Identify logical fallacies in arguments</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Distinguish between facts and opinions</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Evaluate the strength of evidence</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Be aware of your own biases</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-violet-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Real-World Examples:</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>News Analysis:</strong> You see a headline claiming "X causes Y." Critical thinking: Check the source, 
                          look for peer-reviewed studies, consider alternative explanations, and see if correlation equals causation.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Decision Making:</strong> Choosing a college major. Critical approach: Research job markets, 
                          consider your interests and strengths, talk to professionals in the field, evaluate long-term prospects.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Problem Solving:</strong> Your group project is failing. Critical thinking: Identify root causes, 
                          not just symptoms; consider all stakeholders' perspectives; evaluate multiple solutions before choosing.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Questioning Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Lightbulb className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-3" />
                  The Art of Questioning
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Types of Questions:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li><strong>Closed Questions:</strong> "Did you finish your homework?" - Get specific yes/no answers</li>
                      <li><strong>Open Questions:</strong> "How did you approach the problem?" - Encourage detailed responses</li>
                      <li><strong>Probing Questions:</strong> "Why do you think that?" - Dig deeper into reasoning</li>
                      <li><strong>Clarifying Questions:</strong> "Can you explain what you mean by...?" - Ensure understanding</li>
                      <li><strong>Hypothetical Questions:</strong> "What if...?" - Explore possibilities and scenarios</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">The 5 Ws and H:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="p-2 bg-white dark:bg-gray-800 rounded">
                        <strong>Who:</strong> Identifies people involved
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-800 rounded">
                        <strong>What:</strong> Identifies the subject or action
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-800 rounded">
                        <strong>When:</strong> Establishes time context
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-800 rounded">
                        <strong>Where:</strong> Establishes location context
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-800 rounded">
                        <strong>Why:</strong> Explores reasons and causes
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-800 rounded">
                        <strong>How:</strong> Explores methods and processes
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Practical Actions for Better Questioning:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li className="flex items-start"><span className="mr-2">•</span> Ask questions when you don't understand - no question is too basic</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Follow up questions with "Why?" or "How?" to dig deeper</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Question assumptions - "Is this always true?"</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Ask for evidence: "What supports this claim?"</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Consider alternative perspectives: "What would someone who disagrees say?"</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Question your own beliefs regularly</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Real-World Examples:</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>In Class:</strong> Instead of "I don't understand," ask "Can you explain how X connects to Y?" 
                          This shows you've thought about it and helps the teacher provide targeted help.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Research:</strong> When reading a study, ask: "Who funded this? What's the sample size? 
                          Are there conflicting studies?" This helps you evaluate credibility.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Discussions:</strong> In debates, ask: "What evidence supports your position? What are the 
                          limitations of this view? What would change your mind?" This promotes deeper dialogue.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Common Fallacies */}
              <div className="mb-6 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Common Logical Fallacies to Avoid:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Ad Hominem:</strong> Attacking the person instead of the argument
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Straw Man:</strong> Misrepresenting someone's argument to make it easier to attack
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>False Dichotomy:</strong> Presenting only two options when more exist
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Appeal to Authority:</strong> Using an authority figure as proof (without evidence)
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Correlation vs Causation:</strong> Assuming A causes B just because they're related
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Bandwagon:</strong> Believing something because many others do
                  </div>
                </div>
              </div>

              {/* Development Tips */}
              <div className="mt-6 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Developing Critical Thinking Skills:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside">
                  <li>Read widely and from diverse perspectives</li>
                  <li>Practice active listening - don't just wait to speak</li>
                  <li>Engage in debates and discussions regularly</li>
                  <li>Solve puzzles and brain teasers</li>
                  <li>Write regularly - writing clarifies thinking</li>
                  <li>Challenge your own assumptions regularly</li>
                  <li>Learn about logical fallacies and cognitive biases</li>
                </ul>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowCriticalThinking(false)}
                  className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Education & Teachers Modal */}
      {showEducationTeachers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <GraduationCap className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">Education & Teachers</h2>
                    <p className="text-sm text-white/90 mt-1">The Foundation of Progress and Growth</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEducationTeachers(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 p-4 bg-teal-50 dark:bg-teal-900/20 border-l-4 border-teal-500 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>The Value of Education:</strong> Education is not just about acquiring knowledge—it's about developing 
                  character, building skills, and creating opportunities. Teachers are the architects of this transformation, 
                  guiding students toward their potential and contributing to society's progress.
                </p>
              </div>

              {/* Education Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <GraduationCap className="w-6 h-6 text-teal-600 dark:text-teal-400 mr-3" />
                  Understanding Education
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-teal-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">What is Education?</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Education is the process of facilitating learning, acquiring knowledge, skills, values, beliefs, and habits. 
                      It's a lifelong journey that shapes who we become and how we contribute to society.
                    </p>
                  </div>

                  <div className="border-l-4 border-teal-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Key Benefits of Education:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside">
                      <li><strong>Personal Growth:</strong> Develops critical thinking, creativity, and problem-solving skills</li>
                      <li><strong>Economic Opportunity:</strong> Opens doors to better careers and financial stability</li>
                      <li><strong>Social Awareness:</strong> Helps understand different perspectives and cultures</li>
                      <li><strong>Self-Confidence:</strong> Builds knowledge that empowers decision-making</li>
                      <li><strong>Civic Responsibility:</strong> Creates informed citizens who contribute to democracy</li>
                      <li><strong>Health & Well-being:</strong> Educated people make better health choices</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-teal-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How to Value Education:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li className="flex items-start"><span className="mr-2">•</span> Attend classes regularly and be punctual</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Complete assignments on time and to the best of your ability</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Ask questions when you don't understand</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Participate actively in discussions</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Take care of educational materials and resources</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Respect the learning environment</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Help classmates who are struggling</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Apply what you learn in real-life situations</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-teal-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Real-World Examples:</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Academic Success:</strong> A student who values education doesn't skip classes, takes notes 
                          actively, reviews material regularly, and seeks help when needed. This shows respect for the learning process.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Beyond Grades:</strong> Education is valuable when you apply math skills to manage finances, 
                          use science knowledge to make health decisions, or use history lessons to understand current events.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Lifelong Learning:</strong> Valuing education means continuing to learn after formal schooling—reading books, 
                          taking online courses, attending workshops, and staying curious throughout life.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Teachers Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <GraduationCap className="w-6 h-6 text-cyan-600 dark:text-cyan-400 mr-3" />
                  The Role of Teachers
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-cyan-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Why Teachers Matter:</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Teachers are more than just instructors—they are mentors, role models, motivators, and guides. They shape 
                      not just what we know, but who we become.
                    </p>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside mt-2">
                      <li>They ignite curiosity and passion for learning</li>
                      <li>They provide guidance and support during difficult times</li>
                      <li>They recognize and nurture individual talents</li>
                      <li>They prepare students for real-world challenges</li>
                      <li>They build confidence and self-esteem</li>
                      <li>They shape character and values</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-cyan-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How to Respect and Appreciate Teachers:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <li className="flex items-start"><span className="mr-2">•</span> Greet teachers with respect and use proper titles</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Listen attentively when they're speaking</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Follow classroom rules and guidelines</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Complete assignments and homework on time</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Participate actively in class discussions</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Ask questions respectfully and wait for your turn</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Don't interrupt or disrupt the class</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Express gratitude for their efforts</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Accept constructive feedback gracefully</li>
                      <li className="flex items-start"><span className="mr-2">•</span> Help maintain classroom cleanliness</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-cyan-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Real-World Examples:</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>In the Classroom:</strong> Instead of talking while the teacher explains, you wait, listen, 
                          take notes, and ask questions at appropriate times. This shows respect for their time and expertise.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>After Class:</strong> A teacher stays late to help you understand a concept. Showing appreciation 
                          by saying "Thank you for your time" and applying their advice demonstrates respect.
                        </p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Outside School:</strong> When you see a teacher outside school, greeting them respectfully and 
                          showing that you remember and value their impact on your life.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-cyan-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Understanding Teacher Challenges:</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <p><strong>Time Investment:</strong> Teachers spend hours preparing lessons, grading papers, and planning activities 
                      beyond classroom hours. Recognizing this effort shows appreciation.</p>
                      <p><strong>Individual Attention:</strong> Teachers manage multiple students with different learning styles and needs. 
                      Being patient and cooperative helps them help everyone.</p>
                      <p><strong>Emotional Labor:</strong> Teachers care about students' success and well-being. Showing respect and 
                      effort validates their commitment.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Teacher's Day & Appreciation */}
              <div className="mb-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-lg border border-teal-200 dark:border-teal-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Ways to Show Appreciation:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Verbal Appreciation:</strong> Simply saying "Thank you" or "I learned a lot from you"
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Written Notes:</strong> A heartfelt thank-you card or letter
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Academic Excellence:</strong> Doing your best work shows you value their teaching
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Classroom Participation:</strong> Engaging actively in lessons
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Helping Others:</strong> Assisting classmates shows you learned values, not just facts
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded">
                    <strong>Long-term Impact:</strong> Staying in touch and sharing your successes later in life
                  </div>
                </div>
              </div>

              {/* Quotes */}
              <div className="mt-6 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Inspirational Thoughts:</h3>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400 italic">
                  <p>"Education is the most powerful weapon which you can use to change the world." - Nelson Mandela</p>
                  <p>"A teacher affects eternity; he can never tell where his influence stops." - Henry Adams</p>
                  <p>"The function of education is to teach one to think intensively and to think critically. Intelligence plus character—that is the goal of true education." - Martin Luther King Jr.</p>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowEducationTeachers(false)}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Inbuilt PDF Viewer - Renders PDF client-side without downloading */}
      {pdfViewerState.open && pdfViewerState.url && (
        <InbuiltPDFViewer
          url={pdfViewerState.url}
          title={pdfViewerState.title}
          onClose={() => {
            if (pdfLoadTimeoutRef.current) {
              clearTimeout(pdfLoadTimeoutRef.current);
              pdfLoadTimeoutRef.current = null;
            }
            setPdfViewerState({ open: false, url: null, title: null, minimized: false });
            setPdfLoadError(false);
            setPdfViewMethod('object');
            pdfViewMethodRef.current = 'object';
          }}
        />
      )}
    </div>
  );
};

export default Resources;
